'use client';
import { cva, type VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { ButtonHTMLAttributes, ReactNode, CSSProperties, useState } from 'react';

const buttonClasses = cva(
  'inline-flex items-center justify-center font-medium transition-all duration-300 ease-out disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group',
  {
    variants: {
      variant: {
        primary: 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-800 active:scale-95',
        secondary: 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 shadow-md hover:shadow-lg hover:from-gray-200 hover:to-gray-300  active:scale-95',
        outline: 'border-2 border-blue-600 bg-transparent text-blue-600 hover:bg-blue-600 hover:text-white active:scale-95',
        danger: 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg hover:shadow-xl hover:from-red-700 hover:to-red-800 active:scale-95',
        ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 active:scale-95',
        gradient: 'bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 text-white shadow-lg hover:shadow-xl active:scale-95',
      },
      size: {
        small: 'px-4 py-2 text-sm rounded-lg',
        medium: 'px-6 py-3 text-base rounded-xl',
        large: 'px-8 py-4 text-lg rounded-2xl',
        xl: 'px-10 py-5 text-xl rounded-2xl',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  }
)

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonClasses> {
  // Required parameters with defaults
  text?: string;
  text_font_size?: string;
  text_font_family?: string;
  text_font_weight?: string;
  text_line_height?: string;
  text_text_align?: string;
  text_color?: string;
  fill_background_color?: string;
  border_border?: string;
  border_border_radius?: string;
  text_text_transform?: string;
  
  // Optional parameters
  layout_align_self?: string;
  layout_gap?: string;
  layout_width?: string;
  padding?: string;
  position?: string;
  
  // Modern button features
  loading?: boolean;
  ripple?: boolean;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  
  // Standard React props
  children?: ReactNode;
  leftImage?: {
    src: string;
    width: number;
    height: number;
  };
  rightImage?: {
    src: string;
    width: number;
    height: number;
  };
}

const Button = ({
  // Required parameters with defaults
  text = "New Chat",
  text_font_size = "text-[16px]",
  text_font_family = "DM Sans",
  text_font_weight = "font-medium",
  text_line_height = "leading-sm",
  text_text_align = "left",
  text_color = "text-primary-foreground",
  fill_background_color = "bg-primary-background",
  border_border = "1px solid #ffffff51",
  border_border_radius = "rounded-xl",
  
  // Optional parameters (no defaults)
  text_text_transform,
  layout_align_self,
  layout_gap,
  layout_width,
  padding,
  position,
  
  // Modern button features
  loading = false,
  ripple = true,
  icon,
  iconPosition = 'left',
  
  // Standard React props
  variant,
  size,
  disabled = false,
  className,
  children,
  onClick,
  type = 'button',
  leftImage,
  rightImage,
  ...props
}: ButtonProps) => {
  const [ripples, setRipples] = useState<Array<{ id: number; x: number; y: number }>>([]);
  // Safe validation for optional parameters
  const hasValidTextTransform = text_text_transform && typeof text_text_transform === 'string' && text_text_transform.trim() !== ''
  const hasValidAlignSelf = layout_align_self && typeof layout_align_self === 'string' && layout_align_self.trim() !== ''
  const hasValidGap = layout_gap && typeof layout_gap === 'string' && layout_gap.trim() !== ''
  const hasValidWidth = layout_width && typeof layout_width === 'string' && layout_width.trim() !== ''
  const hasValidPadding = padding && typeof padding === 'string' && padding.trim() !== ''
  const hasValidPosition = position && typeof position === 'string' && position.trim() !== ''

  const optionalClasses = [
    hasValidTextTransform ? `${text_text_transform}` : '',
    hasValidAlignSelf ? `self-${layout_align_self}` : '',
    hasValidGap ? `gap-[${layout_gap}]` : '',
    hasValidWidth ? (layout_width === 'full' ? 'w-full' : `w-[${layout_width}]`) : '',
    hasValidPadding ? `p-[${padding}]` : '',
    hasValidPosition ? position : '',
  ].filter(Boolean).join(' ')

  // Build custom styles for non-Tailwind properties
  const customStyles: CSSProperties = {
    ...(text_font_family && !text_font_family.startsWith('font-') && { fontFamily: text_font_family }),
  }

  // Build Tailwind classes for styling
  const styleClasses = [
    text_font_size,
    text_font_family.startsWith('font-') ? text_font_family : '',
    text_font_weight,
    text_line_height,
    text_text_align === 'left' ? 'text-left' : text_text_align === 'center' ? 'text-center' : text_text_align === 'right' ? 'text-right' : '',
    text_color,
    // Only apply these if not using variant system
    !variant ? fill_background_color : '',
    !variant && border_border ? 'border border-solid border-[#ffffff51]' : '',
    border_border_radius,
  ].filter(Boolean).join(' ')

  const createRipple = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!ripple) return;
    
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    const newRipple = {
      id: Date.now(),
      x,
      y
    };
    
    setRipples(prev => [...prev, newRipple]);
    
    // Remove ripple after animation
    setTimeout(() => {
      setRipples(prev => prev.filter(ripple => ripple.id !== newRipple.id));
    }, 600);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) {
      event.preventDefault()
      return
    }
    
    createRipple(event);
    
    if (typeof onClick === 'function') {
      onClick(event)
    }
  }

  return (
    <button data-component-id="src/components/ui/Button.tsx:185:4" data-component-path="src/components/ui/Button.tsx" data-component-line="185" data-component-file="Button.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      type={type}
      disabled={disabled || loading}
      onClick={handleClick}
      style={customStyles}
      className={twMerge(
        buttonClasses({ variant, size }),
        styleClasses,
        optionalClasses,
        className
      )}
      aria-disabled={disabled || loading}
      {...props}
    >
      {/* Ripple effects */}
      {ripples.map((ripple) => (
        <span data-component-id="src/components/ui/Button.tsx:201:8" data-component-path="src/components/ui/Button.tsx" data-component-line="201" data-component-file="Button.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20pointer-events-none%20animate-ping%22%7D"
          key={ripple.id}
          className="absolute pointer-events-none animate-ping"
          style={{
            left: ripple.x - 10,
            top: ripple.y - 10,
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            transform: 'scale(0)',
            animation: 'ripple 0.6s linear',
          }}
        />
      ))}
      
      {/* Loading spinner */}
      {loading && (
        <svg data-component-id="src/components/ui/Button.tsx:219:8" data-component-path="src/components/ui/Button.tsx" data-component-line="219" data-component-file="Button.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22animate-spin%20-ml-1%20mr-2%20h-4%20w-4%22%7D"
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle data-component-id="src/components/ui/Button.tsx:225:10" data-component-path="src/components/ui/Button.tsx" data-component-line="225" data-component-file="Button.tsx" data-component-name="circle" data-component-content="%7B%22elementName%22%3A%22circle%22%2C%22className%22%3A%22opacity-25%22%7D"
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path data-component-id="src/components/ui/Button.tsx:233:10" data-component-path="src/components/ui/Button.tsx" data-component-line="233" data-component-file="Button.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%2C%22className%22%3A%22opacity-75%22%7D"
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      
      {/* Left icon or image */}
      {(icon && iconPosition === 'left') && (
        <span data-component-id="src/components/ui/Button.tsx:243:8" data-component-path="src/components/ui/Button.tsx" data-component-line="243" data-component-file="Button.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22mr-2%22%7D" className="mr-2">{icon}</span>
      )}
      
      {leftImage && (
        <img data-component-id="src/components/ui/Button.tsx:247:8" data-component-path="src/components/ui/Button.tsx" data-component-line="247" data-component-file="Button.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22alt%22%3A%22%22%7D" 
          src={leftImage.src} 
          alt="" 
          width={leftImage.width} 
          height={leftImage.height}
          className={`h-auto w-[${leftImage.width}]`}
        />
      )}
      
      {/* Button content */}
      {!loading && (children || text)}
      
      {/* Right icon or image */}
      {(icon && iconPosition === 'right') && (
        <span data-component-id="src/components/ui/Button.tsx:261:8" data-component-path="src/components/ui/Button.tsx" data-component-line="261" data-component-file="Button.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22ml-2%22%7D" className="ml-2">{icon}</span>
      )}
      
      {rightImage && (
        <img data-component-id="src/components/ui/Button.tsx:265:8" data-component-path="src/components/ui/Button.tsx" data-component-line="265" data-component-file="Button.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22alt%22%3A%22%22%2C%22className%22%3A%22ml-2%22%7D" 
          src={rightImage.src} 
          alt="" 
          width={rightImage.width} 
          height={rightImage.height}
          className="ml-2"
        />
      )}
      
      {/* Add CSS for ripple animation */}
      <style data-component-id="src/components/ui/Button.tsx:275:6" data-component-path="src/components/ui/Button.tsx" data-component-line="275" data-component-file="Button.tsx" data-component-name="style" data-component-content="%7B%22elementName%22%3A%22style%22%7D" jsx>{`
        @keyframes ripple {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
      `}</style>
    </button>
  )
}

export default Button